/**
 * @component NewsAdminPanel
 * @description 뉴스 기사를 등록·수정·삭제하는 관리자 패널입니다. 본문은 리치 에디터로 작성하며
 *              이미지와 링크를 본문 안에 삽입할 수 있습니다.
 * @usage AdminPage의 '뉴스' 탭에서 렌더링됩니다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import QuillEditor from '../../components/common/QuillEditor';
import ModalPopup from '../../components/common/ModalPopup';
import { useSeedReset } from '../../hooks/useSeedReset';
import * as adminApi from '../../services/adminApi';
import { AdminAuthError } from '../../services/adminApi';

// [비즈니스] 서버의 허용 분류와 반드시 일치해야 합니다. 서버가 기준이며 여기서는 선택지만 제공합니다.
const CATEGORIES = ['Notice', 'Solution', 'CS', 'News'];

// [기술] HTML date 입력값("2026-05-12")을 저장 형식("2026.05.12")으로 변환합니다.
function formatDateForStorage(htmlDate) {
  return htmlDate.replace(/-/g, '.');
}

// [기술] 저장된 날짜("2026.05.12")를 HTML date 입력 형식("2026-05-12")으로 변환합니다.
function parseStoredDate(storedDate) {
  return storedDate ? storedDate.replace(/\./g, '-') : getTodayHtmlDate();
}

// [기능] 오늘 날짜를 HTML date 입력 형식으로 반환합니다.
// [기술] toISOString()은 UTC 기준이라 한국 시간 오전 9시 이전에는 전날이 나옵니다.
//        로컬 시간대 기준으로 계산해 관리자가 보는 날짜와 일치시킵니다.
function getTodayHtmlDate() {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

// [기능] 저장된 본문을 에디터가 읽을 수 있는 HTML로 정규화합니다.
// [비즈니스] 리치 에디터 도입 이전 기사는 본문이 문단 문자열 배열로 저장돼 있습니다.
//            두 형식을 모두 받아야 과거 기사를 수정할 때 화면이 깨지지 않습니다.
function toEditorHtml(body) {
  if (Array.isArray(body)) {
    // [기술] 기존 본문에 '<'가 들어 있으면 태그로 해석되므로 이스케이프한 뒤 <p>로 감쌉니다.
    return body
      .map((paragraph) => {
        const escaped = String(paragraph)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        return `<p>${escaped}</p>`;
      })
      .join('');
  }
  return typeof body === 'string' ? body : '';
}

function makeEmptyForm() {
  return { category: 'Notice', date: getTodayHtmlDate(), title: '', summary: '', body: '' };
}

// [기능] 기존 편집과 초기화 결과 반영을 제공하는 관리자 패널입니다.
export default function NewsAdminPanel({ onUnauthorized }) {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(makeEmptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [pendingDelete, setPendingDelete] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    let isActive = true;
    adminApi
      .fetchNews()
      .then((items) => {
        if (isActive) setNewsItems(items || []);
      })
      .catch((error) => {
        if (isActive) setListError(error.message);
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, []);

  // [기능] 세션 만료(401)면 로그인 화면으로 돌리고, 그 외 오류는 메시지로 표시합니다.
  const handleError = (error, setMessage) => {
    if (error instanceof AdminAuthError) {
      onUnauthorized();
      return;
    }
    setMessage(error.message);
  };

  // [기능] 에디터가 이미지 버튼을 눌렀을 때 파일을 서버에 올리고 URL을 돌려줍니다.
  // [비즈니스] 서버 왕복 전에 형식과 용량을 먼저 걸러 불필요한 업로드와 대기를 줄입니다.
  const handleImageUpload = useCallback(async (file) => {
    if (!file.type.startsWith('image/')) {
      throw new Error('이미지 파일만 업로드할 수 있습니다.');
    }
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('이미지 용량은 5MB를 넘을 수 없습니다.');
    }
    return adminApi.uploadImage(file);
  }, []);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // [기능] 등록 폼을 새로 엽니다.
  const openCreateForm = () => {
    setEditingSlug(null);
    setForm(makeEmptyForm());
    setFormError('');
    setIsFormOpen(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // [기능] 선택한 기사 내용을 폼에 채우고 수정 모드로 전환합니다.
  const openEditForm = (item) => {
    setEditingSlug(item.slug);
    setForm({
      category: item.category,
      date: parseStoredDate(item.date),
      title: item.title,
      summary: item.summary ?? '',
      body: toEditorHtml(item.body),
    });
    setFormError('');
    setIsFormOpen(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingSlug(null);
    setForm(makeEmptyForm());
    setFormError('');
  };

  // [기능] 등록(POST) 또는 수정(PUT)을 수행합니다. editingSlug 여부로 모드를 구분합니다.
  const handleSubmit = async () => {
    if (!form.title.trim() || !form.date) {
      setFormError('제목과 날짜는 필수입니다.');
      return;
    }

    setIsSubmitting(true);
    setFormError('');
    // [기술] 비동기 처리 중 상태가 바뀔 수 있으므로 현재 모드를 지역 변수로 고정합니다.
    const currentSlug = editingSlug;

    try {
      const payload = {
        category: form.category,
        date: formatDateForStorage(form.date),
        title: form.title.trim(),
        summary: form.summary.trim(),
        body: form.body,
      };

      const saved = currentSlug
        ? await adminApi.updateNews(currentSlug, payload)
        : await adminApi.createNews(payload);

      if (currentSlug) {
        // [비즈니스] 수정 시 목록 내 위치를 유지하면서 해당 항목만 교체합니다.
        setNewsItems((prev) => prev.map((item) => (item.slug === currentSlug ? saved : item)));
      } else {
        // [비즈니스] 최신 기사가 목록 상단에 오도록 서버와 동일하게 맨 앞에 추가합니다.
        setNewsItems((prev) => [saved, ...prev]);
      }
      // [기술] 서버가 정화한 본문과 편집 중 HTML이 다를 수 있어, 중간 상태를 남기지 않고 폼을 닫습니다.
      closeForm();
    } catch (error) {
      handleError(error, setFormError);
    } finally {
      setIsSubmitting(false);
    }
  };

  // [기능] 삭제 확인 후 기사를 제거합니다.
  const handleDeleteConfirmed = async () => {
    const slug = pendingDelete?.slug;
    setPendingDelete(null);
    if (!slug) return;

    try {
      await adminApi.deleteNews(slug);
      setNewsItems((prev) => prev.filter((item) => item.slug !== slug));
      // [비즈니스] 삭제한 기사를 편집 중이었다면 폼도 함께 닫아 유령 상태를 남기지 않습니다.
      if (editingSlug === slug) closeForm();
    } catch (error) {
      handleError(error, setListError);
    }
  };

  const { isResetting, requestReset, dialogProps } = useSeedReset({
    target: 'news',
    isDirty: isFormOpen,
    // [기능] 초기화 응답으로 편집 상태를 교체하고 기존 오류를 지웁니다.
    onApplied: (data) => {
      setListError('');
      setNewsItems(data || []);
      closeForm();
      setPendingDelete(null);
    },
    onError: setListError,
    onUnauthorized,
  });

  if (loading) return <p className="admin-loading">불러오는 중...</p>;

  return (
    <section className="admin-panel">
      <div className="admin-toolbar">
        <div className="admin-toolbar-text">
          <h2 className="admin-toolbar-title">뉴스 관리</h2>
          <p className="admin-toolbar-hint">본문에 이미지와 링크를 넣을 수 있습니다. 등록·수정은 즉시 반영됩니다.</p>
        </div>
        <div className="admin-toolbar-actions">
          {listError && <span className="admin-error">{listError}</span>}
          <button type="button" className="admin-btn admin-btn-danger" onClick={requestReset} disabled={isResetting || isSubmitting}>
            초기화
          </button>
          <button type="button" className="admin-btn admin-btn-primary" onClick={openCreateForm} disabled={isResetting}>
            + 뉴스 추가
          </button>
        </div>
      </div>

      {isFormOpen && (
        <div className="admin-news-form" ref={formRef}>
          <h3 className="admin-news-form-title">{editingSlug ? '뉴스 수정' : '뉴스 등록'}</h3>

          <div className="admin-news-form-grid">
            <label className="admin-field">
              <span className="admin-field-label">분류</span>
              <select
                className="admin-input"
                value={form.category}
                onChange={(e) => updateForm('category', e.target.value)}
              >
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </label>

            <label className="admin-field">
              <span className="admin-field-label">날짜</span>
              <input
                type="date"
                className="admin-input"
                value={form.date}
                onChange={(e) => updateForm('date', e.target.value)}
              />
            </label>

            <label className="admin-field admin-field-wide">
              <span className="admin-field-label">제목</span>
              <input
                type="text"
                className="admin-input"
                value={form.title}
                onChange={(e) => updateForm('title', e.target.value)}
                placeholder="기사 제목"
                maxLength={200}
              />
            </label>
          </div>

          <label className="admin-field">
            <span className="admin-field-label">요약</span>
            <textarea
              className="admin-input admin-textarea"
              value={form.summary}
              onChange={(e) => updateForm('summary', e.target.value)}
              placeholder="목록에 노출되는 한 줄 요약"
              rows={2}
              maxLength={500}
            />
          </label>

          <div className="admin-field">
            <span className="admin-field-label">본문</span>
            {/* [비즈니스] 뉴스 본문에서만 이미지 삽입을 허용합니다. 문의 폼 에디터는 기본값(false)을 유지합니다. */}
            <QuillEditor
              value={form.body}
              onChange={(html) => updateForm('body', html)}
              placeholder="본문을 입력하세요. 툴바 버튼으로 사진과 링크를 넣을 수 있습니다."
              allowImages
              onImageUpload={handleImageUpload}
              useLinkDialog
            />
          </div>

          {formError && <p className="admin-error admin-news-form-error">{formError}</p>}

          <div className="admin-news-form-actions">
            <button type="button" className="admin-btn admin-btn-ghost" onClick={closeForm} disabled={isSubmitting}>
              취소
            </button>
            <button type="button" className="admin-btn admin-btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? '저장 중...' : editingSlug ? '수정 저장' : '등록'}
            </button>
          </div>
        </div>
      )}

      <p className="admin-news-count">총 {newsItems.length}건</p>

      <ul className="admin-news-list">
        {newsItems.map((item) => (
          <li key={item.slug} className="admin-news-item">
            <div className="admin-news-item-main">
              <span className="admin-news-category">{item.category}</span>
              <span className="admin-news-title">{item.title}</span>
              <span className="admin-news-date">{item.date}</span>
            </div>
            <div className="admin-news-item-actions">
              <button type="button" className="admin-btn admin-btn-ghost" onClick={() => openEditForm(item)}>
                수정
              </button>
              <button type="button" className="admin-btn admin-btn-danger" onClick={() => setPendingDelete(item)}>
                삭제
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* [비즈니스] window.confirm은 모바일에서 화면을 가로막고 사이트 디자인과 어긋나므로 공용 모달을 사용합니다. */}
      <ModalPopup
        isOpen={!!pendingDelete}
        title="뉴스 삭제"
        message={pendingDelete ? `'${pendingDelete.title}' 기사를 삭제할까요? 되돌릴 수 없습니다.` : ''}
        confirmLabel="삭제"
        cancelLabel="취소"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
      <ModalPopup {...dialogProps} />
    </section>
  );
}
